import React from "react";

import { GetStaticProps } from "next";
import {
  ATypeNationalMeritHead,
  ATypeNationalMeritSummary,
  BTypeCardView,
  CTypeBreadCrumb,
  FullWidthTwinButton,
  RichSearch,
  call,
  nationalMeritFaqRichSearch,
  GOI_WEB_URL,
  customEvent,
} from "goi_common";
import crematorium from "goi_common/src/constants/hwajang_detail.json";
import facilities from "goi_common/src/constants/facilities_detail.json";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos, bestParlorIds } = parlorRegion;

export const getStaticProps: GetStaticProps = async () => {
  const regionCrematoriums = crematorium.filter((p) =>
    targetSidos.includes(String(p.sido))
  );
  const regionPublicFacilities = facilities.filter(
    (f) => targetSidos.includes(f.sido) && f.is_public
  );
  const regionBestParlors = parlors.filter((p) => bestParlorIds.includes(p.id));
  const description =
    "강원장례정보공개 (고이장례연구소) - 국가유공자는 어떻게 장례 준비를 해야 할까요? 국가유공자 장례 준비, 강원장례정보공개(고이장례연구소)와 함께 준비하세요.";

  return {
    props: {
      regionCrematoriums,
      regionPublicFacilities,
      regionBestParlors,
      pageDescription: description,
      pageKeywords: `국가유공자 장례, 강원 국가유공자 장례, 강원 국가유공자 장례 준비, 강원 국가유공자 장례 비용`,
      pageOgTitle: `강원 국가유공자 장례혜택 정보는 :: 강원장례정보공개(고이장례연구소)와 함께`,
      pageOgDescription: description,
      pageOgImage: "",
    },
  };
};

export default function index({
  regionCrematoriums,
  regionPublicFacilities,
  regionBestParlors,
  pageOgTitle,
}: {
  regionCrematoriums: Array<any>;
  regionPublicFacilities: Array<any>;
  regionBestParlors: Array<any>;
  pageOgTitle: string;
}) {
  const faqList = nationalMeritFaqRichSearch;
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <RichSearch data={faqList} />
      </Head>
      <CTypeBreadCrumb
        firstItem={{
          text: `국가유공자 장례 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "veterans",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `국가유공자 장례헬프센터`,
          href: `/gangwon-veterans/`,
          event: {
            domain: "gangwon",
            path: "veterans",
            trigger_and_target: "br_crumb_veterans",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "veterans",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <Stack
        gap="12px"
        padding="40px 20px 20px"
        margin="10px 12px 20px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeNationalMeritHead
          titleSido="강원"
          mainColor={regionColor}
          title="국가유공자 장례헬프센터"
        />
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "gangwon",
                path: "veterans",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gangwon",
                path: "veterans",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "gangwon",
                path: "veterans",
                trigger_and_target: "goi_call_btom",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gangwon",
                path: "veterans",
                trigger_and_target: "uv_mth_req_btom",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h3 className="subtitle_18_b">강원 지역 장례식장</h3>
        {regionBestParlors.map((res) => (
          <BTypeCardView
            key={res.companyname}
            name={res.companyname}
            mainColor={regionColor}
            href={`https://www.goifuneral.co.kr/match/parlor-request/${res.companyname}/`}
            descriptions={[
              `빈소 평균 비용 : ${Number(
                res.binso_average_charge
              ).toLocaleString()}원`,
            ]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            arrowText="혜택 알아보기"
            event={{
              domain: "gangwon",
              path: "veterans",
              trigger_and_target: "uv_prlr_detail",
            }}
          />
        ))}
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h4 className="subtitle_18_b">강원 국가유공자 무료 화장장</h4>
        {regionCrematoriums.map((res) => (
          <BTypeCardView
            descriptions={["국가유공자 무료"]}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            hasOneDayBasis={false}
            href={`/gangwon-veterans/crema/${res.companyname}/`}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "gangwon",
              path: "veterans",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h4 className="subtitle_18_b">강원 국가유공자 무료 장지 (공설)</h4>
        {regionPublicFacilities.map((res) => (
          <BTypeCardView
            descriptions={["국가유공자 우선 안치"]}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            hasOneDayBasis={false}
            href={`/gangwon-veterans/facility/${res.companyname}/`}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "gangwon",
              path: "veterans",
              trigger_and_target: "uv_facility_detail",
            }}
          />
        ))}
      </Stack>
    </>
  );
}
